import React, { useEffect, useState, ReactNode } from "react";
import PropTypes from "prop-types";
import { NavLink, useLocation } from "react-router-dom";
import classnames from "classnames";
import s from "./LinksGroup.module.scss";

interface ChildLink {
  header?: ReactNode;
  link?: string;
  index?: string;
  childrenLinks?: ChildLink[];
  target?: string;
  [key: string]: any;
}

interface LinksGroupProps {
  header: ReactNode;
  link?: string;
  childrenLinks?: ChildLink[] | null;
  iconName?: string;
  className?: string;
  badge?: string | number;
  label?: string;
  activeItem?: string;
  isHeader?: boolean;
  index?: string;
  deep?: number;
  onActiveSidebarItemChange?: (item: string | null) => void;
  exact?: boolean;
  iconElement?: ReactNode;
  target?: string;
  onNavigate?: () => void;
  sidebarExpanded?: boolean;
}

function pathMatches(pathname: string, link?: string): boolean {
  if (!link) return false;
  return pathname === link || pathname.startsWith(`${link}/`);
}

function LinksGroup({
  header,
  link = "",
  childrenLinks = null,
  iconName,
  className = "",
  badge,
  label = "",
  activeItem = "",
  isHeader = false,
  index,
  deep = 0,
  onActiveSidebarItemChange,
  exact = true,
  iconElement,
  target,
  onNavigate,
  sidebarExpanded = true,
}: LinksGroupProps) {
  const location = useLocation();
  const childActive =
    Array.isArray(childrenLinks) &&
    childrenLinks.some(
      (child) =>
        pathMatches(location.pathname, child.link) ||
        (child.childrenLinks &&
          child.childrenLinks.some((g) =>
            pathMatches(location.pathname, g.link)
          ))
    );

  // Same pattern as sing-app-react: keep a click toggle, but open state
  // is driven by activeItem. When sidebar auto-closes, activeItem is cleared.
  const [headerLinkWasClicked, setHeaderLinkWasClicked] = useState<boolean>(true);

  useEffect(() => {
    // When sidebar auto-collapses, force submenus closed.
    // When it expands again, allow active branch to reopen.
    setHeaderLinkWasClicked(sidebarExpanded);
  }, [sidebarExpanded]);

  const handleNav = () => {
    if (onNavigate) onNavigate();
  };

  const renderIcon = () => (
    <span className={classnames("icon", s.icon)}>
      {iconElement ? (
        iconElement
      ) : (
        <i className={iconName || "mdi mdi-circle-outline"} />
      )}
    </span>
  );

  if (!childrenLinks) {
    if (isHeader) {
      if (target === "_blank" || (link && link.startsWith("http"))) {
        return (
          <li className={classnames("link-wrapper", s.headerLink, className)}>
            <a href={link} target="_blank" rel="noreferrer" onClick={handleNav}>
              {renderIcon()}
              <span className={s.menuText}>{header}</span>
              {label ? (
                <sup className={`${s.headerLabel} ${s.headerUpdate}`}>{label}</sup>
              ) : null}
              {badge ? (
                <span className={`${s.badge} badge rounded-pill bg-danger`}>
                  {badge}
                </span>
              ) : null}
            </a>
          </li>
        );
      }

      return (
        <li className={classnames("link-wrapper", s.headerLink, className)}>
          <NavLink
            to={link}
            className={({ isActive }) =>
              isActive ||
              (exact === false && location.pathname.startsWith(link))
                ? s.headerLinkActive
                : undefined
            }
            end={exact !== false}
            onClick={handleNav}
          >
            {renderIcon()}
            <span className={s.menuText}>{header}</span>
            {label ? (
              <sup className={`${s.headerLabel} ${s.headerUpdate}`}>{label}</sup>
            ) : null}
            {badge ? (
              <span className={`${s.badge} badge rounded-pill bg-danger`}>
                {badge}
              </span>
            ) : null}
          </NavLink>
        </li>
      );
    }

    return (
      <li className={s.subLink}>
        <NavLink
          to={link}
          className={({ isActive }) => (isActive ? s.headerLinkActive : undefined)}
          style={{ paddingLeft: `${26 + 10 * Math.max(deep - 1, 0)}px` }}
          end={exact !== false}
          onClick={handleNav}
        >
          {header}
          {label ? <sup className={s.headerLabel}>{label}</sup> : null}
        </NavLink>
      </li>
    );
  }

  const match = childActive || pathMatches(location.pathname, link);

  const togglePanelCollapse = () => {
    if (!sidebarExpanded) return;
    if (onActiveSidebarItemChange) {
      onActiveSidebarItemChange(link);
    }
    setHeaderLinkWasClicked(
      !headerLinkWasClicked ||
        !(activeItem && index && String(activeItem).includes(index))
    );
  };

  // Match sing-app-react: submenu opens only while expanded + activeItem matches
  const isOpen =
    sidebarExpanded &&
    !!activeItem &&
    !!index &&
    String(activeItem).includes(index) &&
    headerLinkWasClicked;

  return (
    <li
      className={classnames(
        "link-wrapper",
        { [s.headerLink]: isHeader },
        className
      )}
    >
      <a
        href="#submenu"
        className={classnames(
          { [s.headerLinkActive]: match },
          { [s.collapsed]: isOpen },
          "d-flex"
        )}
        style={{
          paddingLeft: `${deep === 0 ? 50 : 26 + 10 * (deep - 1)}px`,
        }}
        onClick={(e) => {
          e.preventDefault();
          togglePanelCollapse();
        }}
      >
        {isHeader ? renderIcon() : null}
        <span className={s.menuText}>{header}</span>
        {label ? (
          <sup className={`${s.headerLabel} ${s.headerNode}`}>{label}</sup>
        ) : null}
        <b className={classnames("fa", "fa-angle-left", s.caret)} />
      </a>
      <div
        className={classnames(s.panel, {
          [s.panelOpen]: isOpen,
          [s.panelHidden]: !sidebarExpanded,
        })}
        aria-hidden={!isOpen}
      >
        <ul>
          {childrenLinks.map((child, ind) => (
            <LinksGroup
              onActiveSidebarItemChange={onActiveSidebarItemChange}
              activeItem={activeItem}
              header={child.header}
              link={child.link}
              index={child.index}
              childrenLinks={child.childrenLinks}
              deep={deep + 1}
              key={child.link || ind}
              onNavigate={onNavigate}
              target={child.target}
              sidebarExpanded={sidebarExpanded}
            />
          ))}
        </ul>
      </div>
    </li>
  );
}

LinksGroup.propTypes = {
  header: PropTypes.node.isRequired,
  link: PropTypes.string,
  childrenLinks: PropTypes.array,
  iconName: PropTypes.string,
  className: PropTypes.string,
  badge: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
  activeItem: PropTypes.string,
  isHeader: PropTypes.bool,
  index: PropTypes.string,
  deep: PropTypes.number,
  onActiveSidebarItemChange: PropTypes.func,
  exact: PropTypes.bool,
  iconElement: PropTypes.node,
  target: PropTypes.string,
  onNavigate: PropTypes.func,
  sidebarExpanded: PropTypes.bool,
};

export default LinksGroup;
